import type { SupplyTableProduct, SupplyTableResponse } from '@/services/supplyService';

/** Mutaciones locales que no deben pisarse mientras corre el bulk de catálogo. */
export type SupplyRowLocalOverride = {
  is_validated?: boolean;
  cantidad_sugerida_usuario?: number | null;
};

export function patchSupplyRowUserQty(
  row: SupplyTableProduct,
  qty: number | null,
): SupplyTableProduct {
  const supplierId = row.supplier_id;
  return {
    ...row,
    cantidad_sugerida_usuario: qty,
    available_suppliers: (row.available_suppliers ?? []).map((supplier) => {
      if (supplierId == null || supplier.supplier_id !== supplierId) return supplier;
      return { ...supplier, cantidad_usuario: qty };
    }),
  };
}

export function patchSupplyRowValidation(
  row: SupplyTableProduct,
  isValidated: boolean,
): SupplyTableProduct {
  const sqId = row.suggested_quantity_id;
  const supplierId = row.supplier_id;
  return {
    ...row,
    is_validated: isValidated,
    available_suppliers: (row.available_suppliers ?? []).map((supplier) => {
      const sameSq = sqId != null && supplier.suggested_quantity_id === sqId;
      const sameSupplier = supplierId != null && supplier.supplier_id === supplierId;
      if (!sameSq && !sameSupplier) return supplier;
      return { ...supplier, is_validated: isValidated };
    }),
  };
}

export function applySupplyRowLocalOverrides(
  rows: SupplyTableProduct[],
  overrides: Map<number, SupplyRowLocalOverride>,
): SupplyTableProduct[] {
  if (overrides.size === 0) return rows;
  return rows.map((row) => {
    const override = overrides.get(row.id);
    if (!override) return row;

    let next = row;
    if (override.is_validated !== undefined && row.is_validated !== override.is_validated) {
      next = patchSupplyRowValidation(next, override.is_validated);
    }
    if (
      override.cantidad_sugerida_usuario !== undefined
      && row.cantidad_sugerida_usuario !== override.cantidad_sugerida_usuario
    ) {
      next = patchSupplyRowUserQty(next, override.cantidad_sugerida_usuario);
    }
    return next;
  });
}

/** Quita overrides ya confirmados por una foto de red fresca (mismo valor). */
export function reconcileSupplyRowLocalOverrides(
  rows: SupplyTableProduct[],
  overrides: Map<number, SupplyRowLocalOverride>,
): void {
  if (overrides.size === 0) return;
  rows.forEach((row) => {
    const override = overrides.get(row.id);
    if (!override) return;
    const remaining: SupplyRowLocalOverride = { ...override };
    if (
      remaining.is_validated !== undefined
      && row.is_validated === remaining.is_validated
    ) {
      delete remaining.is_validated;
    }
    if (
      remaining.cantidad_sugerida_usuario !== undefined
      && row.cantidad_sugerida_usuario === remaining.cantidad_sugerida_usuario
    ) {
      delete remaining.cantidad_sugerida_usuario;
    }
    if (
      remaining.is_validated === undefined
      && remaining.cantidad_sugerida_usuario === undefined
    ) {
      overrides.delete(row.id);
    } else {
      overrides.set(row.id, remaining);
    }
  });
}

export function mergeSupplyTableRowsById(
  existing: SupplyTableProduct[],
  incoming: SupplyTableProduct[],
): SupplyTableProduct[] {
  const byId = new Map<number, SupplyTableProduct>();
  existing.forEach((row) => byId.set(row.id, row));
  incoming.forEach((row) => byId.set(row.id, row));
  return Array.from(byId.values());
}

export function withMergedSupplyRows(
  base: SupplyTableResponse,
  incomingRows: SupplyTableProduct[],
): SupplyTableResponse {
  const data = mergeSupplyTableRowsById(base.data ?? [], incomingRows);
  return {
    ...base,
    data,
    count: data.length,
  };
}
